
import React, { useState } from 'react';
import EmergencyConfirmModal, { EmergencyAction } from './EmergencyConfirmModal';

import { 
  Link2, 
  DollarSign, 
  ShieldAlert, 
  Zap, 
  Layers, 
  RefreshCw, 
  Clock, 
  FileText, 
  PieChart, 
  Bell, 
  Lock, 
  AlertTriangle,
  ChevronRight,
  Plus,
  CheckCircle2,
  Trash2,
  ShieldCheck,
  BarChart3,
  Activity,
  Target,
  User,
  Brain,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../../lib/utils';
import { TRADING_STYLES } from '../../constants';
// 2026-06-06 alpaca-paper-real-account-sync — capital tab now reads the
// real Alpaca paper account snapshot. Hardcoded $120,000 / $50,000 /
// 41.6% are gone; numbers are em-dashes when no broker is connected or
// sync is pending/failed.
// 2026-06-06 source-of-truth-full-ui — Brokerage card now shows
// connected-state info (Mode/Live/Sync) when the viewer has an
// active Alpaca paper row. Driven by viewer.broker_connected
// (see useViewerPortfolio).
import useViewerPortfolio from '../../hooks/useViewerPortfolio';
import { apiV1Base } from '../../lib/apiConfig';
import useUserSettings from '../../lib/useUserSettings';
import { safeFormatTimestamp } from '../../lib/accountFormatters';

const EM_DASH = '—';
function _parseUsd(s: string | number | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = typeof s === 'number' ? s : Number(String(s).replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}
function _fmtUsd(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function CapitalConfigSection({
  localProfile,
  setLocalProfile,
}: {
  localProfile: any;
  setLocalProfile: (p: any) => void;
}) {
  const { data: viewer, refetch } = useViewerPortfolio();
  const syncStatus = (viewer as any).sync_status as
    | 'not_connected' | 'sync_pending' | 'synced' | 'sync_failed' | undefined;
  const acct = (viewer as any).account as any || null;
  const portfolioValueN = _parseUsd(acct?.portfolio_value ?? acct?.equity ?? null);
  const cashN = _parseUsd(acct?.cash ?? null);
  const buyingPowerN = _parseUsd(acct?.buying_power ?? null);

  // Allocation comes from the user's profile (no backend store yet); we
  // never invent a default of $50,000 or 41.6%.
  const rawAllocated = localProfile?.allocatedCapital;
  const allocatedN = _parseUsd(rawAllocated);

  const isSynced = syncStatus === 'synced';
  const isConnectedNoSync = !isSynced &&
    (syncStatus === 'sync_pending' || syncStatus === 'sync_failed');
  const noBroker = !syncStatus || syncStatus === 'not_connected';

  const allocPctOfPv =
    allocatedN !== null && portfolioValueN && portfolioValueN > 0
      ? (allocatedN / portfolioValueN) * 100
      : null;

  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState<string | null>(null);
  const [allocationInput, setAllocationInput] = React.useState<string>(
    rawAllocated !== undefined && rawAllocated !== null && rawAllocated !== ''
      ? String(rawAllocated)
      : '',
  );
  React.useEffect(() => {
    if (rawAllocated !== undefined && rawAllocated !== null) {
      setAllocationInput(String(rawAllocated));
    }
  }, [rawAllocated]);

  // 2026-06-07 revert+save: backend persistence for capital_allocation.
  const _capitalSettings = useUserSettings<{ allocated_usd?: number; cleared?: boolean }>('capital_allocation');
  const [_capitalSavedAt, _setCapitalSavedAt] = React.useState<boolean>(false);
  const [_capitalSaveError, _setCapitalSaveError] = React.useState<string | null>(null);
  // Hydrate input from saved value when none came from profile.
  React.useEffect(() => {
    if (_capitalSettings.loading) return;
    const saved = _capitalSettings.payload;
    if (!saved) return;
    if (saved.cleared) {
      // Saved-as-cleared: only override the input if the user hasn't typed.
      if (!allocationInput && !rawAllocated) setAllocationInput('');
    } else if (typeof saved.allocated_usd === 'number') {
      const str = String(saved.allocated_usd);
      if (!allocationInput && !rawAllocated) setAllocationInput(str);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_capitalSettings.loading, _capitalSettings.payload]);
  const _capitalSavedPayload = _capitalSettings.payload || null;
  const _capitalParsedCurrent = (() => {
    const cleaned = (allocationInput || '').replace(/[,$\s]/g, '');
    const n = Number(cleaned);
    return cleaned === '' ? null : (Number.isFinite(n) ? n : null);
  })();
  const _capitalSavedNumber = _capitalSavedPayload && !_capitalSavedPayload.cleared
    ? (typeof _capitalSavedPayload.allocated_usd === 'number' ? _capitalSavedPayload.allocated_usd : null)
    : null;
  const _capitalDirty = (() => {
    if (_capitalSettings.loading) return false;
    if (_capitalSavedPayload?.cleared) return _capitalParsedCurrent !== null && _capitalParsedCurrent > 0;
    return _capitalParsedCurrent !== _capitalSavedNumber;
  })();

  const handleRefresh = async () => {
    if (refreshing || noBroker) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      if (!token) { setRefreshError('not authenticated'); return; }
      let syncOk = false;
      try {
        const r = await fetch(apiV1Base() + '/user/broker-connections/alpaca-paper/sync', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        syncOk = r.ok;
        if (!r.ok) setRefreshError(`HTTP ${r.status}`);
      } catch (e: any) { setRefreshError(String(e?.message || e)); }
      try { if (typeof refetch === 'function') refetch(); } catch { /* ignore */ }
      try {
        const evts = await import('../../lib/refreshEvents');
        evts.emitRefresh('portfolio', { syncOk });
      } catch { /* ignore */ }
    } finally {
      setRefreshing(false);
    }
  };

  const headlineLabel = isSynced
    ? 'Total Portfolio Value'
    : isConnectedNoSync
    ? 'Total Portfolio Value (sync pending)'
    : 'Total Portfolio Value';
  const headlineValue =
    isSynced && portfolioValueN !== null ? _fmtUsd(portfolioValueN) : EM_DASH;
  const headlineSub = isSynced
    ? `Synced from Alpaca Paper${acct?.account_number_masked ? ' ' + acct.account_number_masked : ''}`
    : isConnectedNoSync
    ? syncStatus === 'sync_failed'
      ? 'Broker connected. Balance sync unavailable.'
      : 'Broker connected. Waiting for Alpaca balance sync.'
    : 'No broker connected';

  const allocLabel = 'Allocated for Trading';
  const allocValue =
    allocatedN !== null
      ? _fmtUsd(allocatedN)
      : (isConnectedNoSync || noBroker ? EM_DASH : EM_DASH);
  const allocSub =
    allocatedN === null
      ? 'No allocation configured'
      : allocPctOfPv !== null
      ? `${allocPctOfPv.toFixed(1)}% of Total Balance`
      : 'Set as fixed USD; % shown after first sync';

  return (
    <div
      data-testid="portfolio-settings-capital"
      data-sync-status={String(syncStatus ?? 'unknown')}
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
    >
      <SectionHeader
        title="Portfolio Capital Configuration"
        icon={DollarSign}
        description="Define how much of your brokerage balance is allocated to Adversyn strategies."
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            data-testid="capital-portfolio-value"
            className="p-6 bg-[var(--app-text)] rounded-2xl text-[var(--card-bg)]"
          >
            <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-2">
              {headlineLabel}
            </div>
            <div className="text-3xl font-black italic">{headlineValue}</div>
            <div className="text-[10px] font-bold text-[var(--muted-text)] opacity-60 uppercase mt-1">
              {headlineSub}
            </div>
          </div>
          <div
            data-testid="capital-allocated"
            className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl"
          >
            <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2">
              {allocLabel}
            </div>
            <div className="text-3xl font-black text-[var(--app-text)] italic">{allocValue}</div>
            <div className="text-[10px] font-bold text-emerald-600/60 uppercase mt-1">
              {allocSub}
            </div>
          </div>
        </div>

        {!noBroker && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <button
                data-testid="capital-refresh-broker-data"
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-black uppercase tracking-widest text-[var(--app-text)] hover:bg-[var(--card-border)] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Refreshing…' : 'Refresh Broker Data'}
              </button>
              {!isSynced && (
                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                  {syncStatus === 'sync_failed' ? 'Sync unavailable' : 'Sync pending'}
                </span>
              )}
            </div>
            {refreshError && (
              <div data-testid="capital-refresh-error" className="text-[10px] font-bold uppercase tracking-widest text-rose-600">
                Refresh failed: {refreshError}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">
              Adjust Allocation (USD)
            </label>
            <div className="relative">
              <DollarSign
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-text)] opacity-60"
                size={16}
              />
              <input
                data-testid="capital-allocation-input"
                type="text"
                value={allocationInput}
                placeholder="No allocation configured"
                onChange={(e) => setAllocationInput(e.target.value)}
                className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl pl-10 pr-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="capital-save-allocation"
              disabled={_capitalSettings.saving}
              onClick={async () => {
                const cleaned = allocationInput.replace(/[,$\s]/g, '');
                const parsed = _parseUsd(cleaned);
                if (parsed === null || parsed < 0) return;
                _setCapitalSaveError(null);
                _setCapitalSavedAt(false);
                setLocalProfile({ ...localProfile, allocatedCapital: String(parsed) });
                logInteraction('PortfolioSettings', 'Set Capital Allocation', { allocation: parsed });
                const r = await _capitalSettings.save({ allocated_usd: parsed, cleared: false });
                if (r.ok) {
                  _setCapitalSavedAt(true);
                  window.setTimeout(() => _setCapitalSavedAt(false), 3000);
                } else {
                  _setCapitalSaveError(r.error || 'Save failed');
                }
              }}
              className="flex-1 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all disabled:opacity-50"
            >
              {_capitalSettings.saving ? 'Saving…' : 'Update Allocation'}
            </button>
            <button
              data-testid="capital-clear-allocation"
              disabled={_capitalSettings.saving}
              onClick={async () => {
                _setCapitalSaveError(null);
                _setCapitalSavedAt(false);
                setAllocationInput('');
                setLocalProfile({ ...localProfile, allocatedCapital: '' });
                logInteraction('PortfolioSettings', 'Clear Capital Allocation', {});
                const r = await _capitalSettings.save({ allocated_usd: 0, cleared: true });
                if (r.ok) {
                  _setCapitalSavedAt(true);
                  window.setTimeout(() => _setCapitalSavedAt(false), 3000);
                } else {
                  _setCapitalSaveError(r.error || 'Clear failed');
                }
              }}
              className="flex-1 py-3 border border-[var(--card-border)] text-[var(--muted-text)] rounded-xl text-xs font-black uppercase italic hover:bg-[var(--input-bg)] transition-all disabled:opacity-50"
            >
              Clear Allocation
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
            {_capitalDirty && !_capitalSettings.saving && (
              <span data-testid="capital-dirty-indicator" className="text-amber-600">Unsaved changes</span>
            )}
            {_capitalSavedAt && !_capitalSaveError && (
              <span data-testid="capital-save-success" className="text-emerald-700">Saved</span>
            )}
            {(_capitalSaveError || _capitalSettings.error) && (
              <span data-testid="capital-save-error" className="text-rose-700">{_capitalSaveError || _capitalSettings.error}</span>
            )}
            <span data-testid="capital-updated-at" className="ml-auto text-[var(--muted-text)]">
              {_capitalSettings.updatedAtUtc
                ? 'Saved ' + safeFormatTimestamp(_capitalSettings.updatedAtUtc, { mode: 'datetime', fallback: '—', invalidFallback: '—' })
                : 'Not yet saved'}
            </span>
          </div>
          <p className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest opacity-70">
            Allocation is a local-only target. Adversyn does not place real or
            paper orders without separate explicit approval.
          </p>
        </div>
      </div>
    </div>
  );
}


const SectionHeader = ({ title, icon: Icon, description }: { title: string, icon: any, description?: string }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-1">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600">
        <Icon size={18} />
      </div>
      <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">{title}</h3>
    </div>
    {description && <p className="text-xs text-[var(--muted-text)] font-medium ml-10">{description}</p>}
  </div>
);

const Toggle = ({ enabled, onChange }: { enabled: boolean, onChange: () => void }) => (
  <button 
    onClick={onChange}
    className={cn(
      "w-10 h-5 rounded-full relative transition-all duration-200",
      enabled ? "bg-emerald-600" : "bg-[var(--input-border)]"
    )}
  >
    <div className={cn(
      "absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm transition-all duration-200",
      enabled ? "right-1" : "left-1"
    )} />
  </button>
);


// 2026-06-07 revert+save: minimal per-section Save bar.
// Lives at the bottom of each section card. Doesn't alter the visible
// section layout above it. Sits on a single short row: dirty + Save + saved-at.
function _SectionSaveBar({
  section,
  buildPayload,
  testid,
}: {
  section: any;
  buildPayload: () => Record<string, any>;
  testid?: string;
}) {
  const settings = useUserSettings<Record<string, any>>(section);
  const [savedJustNow, setSavedJustNow] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const onSave = async () => {
    setLocalError(null);
    setSavedJustNow(false);
    const payload = buildPayload();
    const r = await settings.save(payload);
    if (r.ok) {
      setSavedJustNow(true);
      window.setTimeout(() => setSavedJustNow(false), 3000);
    } else {
      setLocalError(r.error || "Save failed");
    }
  };
  const err = settings.error || localError;
  return (
    <div
      data-testid={testid || ("save-bar-" + String(section))}
      data-section={String(section)}
      className="mt-6 flex flex-wrap items-center gap-3 pt-4 border-t border-[var(--card-border)]"
    >
      <button
        type="button"
        data-testid={"save-button-" + String(section)}
        onClick={() => { void onSave(); }}
        disabled={settings.loading || settings.saving}
        className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {settings.saving ? "Saving…" : "Save"}
      </button>
      {savedJustNow && !err && (
        <span data-testid={"save-success-" + String(section)} className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Saved</span>
      )}
      {err && (
        <span data-testid={"save-error-" + String(section)} className="text-[10px] font-black uppercase tracking-widest text-rose-700">{err}</span>
      )}
      <span data-testid={"save-updated-at-" + String(section)} className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        {settings.updatedAtUtc
          ? "Saved " + safeFormatTimestamp(settings.updatedAtUtc, { mode: "datetime", fallback: "\u2014", invalidFallback: "\u2014" })
          : "Not yet saved"}
      </span>
    </div>
  );
}

export default function PortfolioSettings({ profile, onUpdateProfile }: { profile: any, onUpdateProfile: (p: any) => void }) {
  const [activeSection, setActiveSection] = useState('brokerage');
  // 2026-06-07 settings-emergency-quotes-refresh-final - emergency modal state
  const [emergencyModal, setEmergencyModal] = useState<EmergencyAction | null>(null);
  // 2026-06-06 l-crash-and-heatmap-purge — hook call MUST live here
  // (not in IIFEs below) per Rules of Hooks. References at lines
  // ~598 + ~617 consume `viewerForBrokerHeading?.data`.
  const viewerForBrokerHeading = useViewerPortfolio();
  const [localProfile, setLocalProfile] = useState(profile);
  const [showNotification, setShowNotification] = useState(false);
  const [assetPermissions, setAssetPermissions] = useState({
    Stocks: true,
    ETFs: true,
    Options: false,
    Futures: false,
    Crypto: false,
    'Short Selling': true
  });
  const [marginEnabled, setMarginEnabled] = useState(true);
  const [rebalanceEnabled, setRebalanceEnabled] = useState(true);
  const [rebalanceFreq, setRebalanceFreq] = useState('Weekly');
  const [syncFreq, setSyncFreq] = useState('Real-Time Sync');
  const [taxPref, setTaxPref] = useState('FIFO');
  const [analyticsPrefs, setAnalyticsPrefs] = useState({
    'Risk Metrics': true,
    'Sector Allocation': true,
    'Correlation Analysis': false,
    'Factor Exposure': true,
    'Volatility Analysis': true,
    'ESG Scoring': false
  });
  const [aiPermissions, setAiPermissions] = useState({
    'Allow AI Analysis of Portfolio': true,
    'Allow AI Trade Recommendations': true,
    'Allow Anonymous Benchmarking': false
  });
  const [notificationTriggers, setNotificationTriggers] = useState({
    'Position hits stop level': true,
    'Position reaches profit target': true,
    'Daily loss limit triggered': true,
    'Margin usage high (>80%)': true,
    'New AI strategy recommendation': true
  });
  const [notificationChannels, setNotificationChannels] = useState({
    'In-Platform': true,
    'Email': true,
    'Mobile Push': false
  });

  const sections = [
    { id: 'brokerage', label: 'Broker Connections', icon: Link2 },
    { id: 'capital', label: 'Capital Allocation', icon: DollarSign },
    { id: 'profile', label: 'Trading Profile', icon: User },
    { id: 'risk', label: 'Risk Limits', icon: ShieldAlert },
    { id: 'leverage', label: 'Leverage Controls', icon: Zap },
    { id: 'strategy', label: 'Strategy Allocation', icon: Layers },
    { id: 'rebalancing', label: 'Rebalancing', icon: RefreshCw },
    { id: 'sync', label: 'Trade Sync', icon: Clock },
    { id: 'tax', label: 'Tax Lot Prefs', icon: FileText },
    { id: 'analytics', label: 'Analytics Prefs', icon: PieChart },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'AI Permissions', icon: Lock },
    { id: 'emergency', label: 'Emergency Controls', icon: AlertTriangle },
  ];

  // Removed in WO3 user-data-isolation: brokerage section no longer renders
  // a fake "Alpaca Securities (****3921)" connected card or a default-broker
  // dropdown. State left in place as `_selectedBroker` for compatibility
  // with any external read; it is not surfaced anywhere user-facing.
  const [_selectedBroker] = useState<string>('');
  const [maxLeverage, setMaxLeverage] = useState('2x');
  // 2026-06-07 revert+save: backend persistence for trading_profile.
  const _profileSettings = useUserSettings<Record<string, any>>('trading_profile');
  const [_profileSaveError, _setProfileSaveError] = React.useState<string | null>(null);
  const [_profileSavedJustNow, _setProfileSavedJustNow] = React.useState<boolean>(false);
  // Hydrate localProfile from saved payload on first load. We only
  // overwrite fields that the saved payload has; user-set values stay.
  React.useEffect(() => {
    if (_profileSettings.loading) return;
    const saved = _profileSettings.payload;
    if (!saved || Object.keys(saved).length === 0) return;
    onUpdateProfile({ ...localProfile, ...saved });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_profileSettings.loading, _profileSettings.payload]);

  const handleUpdateProfile = async () => {
    onUpdateProfile(localProfile);
    _setProfileSaveError(null);
    _setProfileSavedJustNow(false);
    // Persist a focused subset of localProfile to the trading_profile
    // section. Backend validator allows holding_period / trade_frequency /
    // strategy_bias / execution_pref / position_size_model / profile_type.
    const payload: Record<string, any> = {};
    if (localProfile?.style) payload.profile_type = String(localProfile.style);
    if (localProfile?.holdingPeriod) payload.holding_period = String(localProfile.holdingPeriod);
    if (localProfile?.tradeFrequency) payload.trade_frequency = String(localProfile.tradeFrequency);
    if (localProfile?.strategyBias) payload.strategy_bias = String(localProfile.strategyBias);
    if (localProfile?.executionPref) payload.execution_pref = String(localProfile.executionPref);
    if (localProfile?.positionModel) payload.position_size_model = String(localProfile.positionModel);
    const r = await _profileSettings.save(payload);
    if (r.ok) {
      _setProfileSavedJustNow(true);
      setShowNotification(true);
      setTimeout(() => { setShowNotification(false); _setProfileSavedJustNow(false); }, 3000);
    } else {
      _setProfileSaveError(r.error || 'Save failed');
    }
  };

  const getAICalibration = (styleId: string) => {
    switch (styleId) {
      case 'quant':
        return [
          'Statistical signals',
          'Volatility factors',
          'Correlation analysis',
          'Multi-factor scoring models'
        ];
      case 'day':
        return [
          'Intraday momentum',
          'Order flow signals',
          'Short-term volatility spikes',
          'Liquidity shifts'
        ];
      case 'scalper':
        return [
          'Micro-structure imbalances',
          'Bid-ask spread dynamics',
          'Tick-level momentum',
          'High-frequency volume clusters'
        ];
      case 'swing':
        return [
          'Multi-day trend exhaustion',
          'Support/Resistance pivots',
          'Sector rotation flows',
          'Earnings cycle positioning'
        ];
      default:
        return [
          'Trend identification',
          'Risk-adjusted opportunity scoring',
          'Market regime detection',
          'Dynamic position sizing'
        ];
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="space-y-6">
            {/* Section 1: Trading Style */}
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="Trading Style" 
                icon={User} 
                description="Select the trading style that best represents your strategy approach. This helps the AI council tailor analysis and opportunity filtering."
              />
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Select Profile Type</label>
                  <select 
                    value={localProfile.style}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Profile Type', { style: e.target.value });
                      setLocalProfile({ ...localProfile, style: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {TRADING_STYLES.map(style => (
                      <option key={style.id} value={style.id}>{style.name}</option>
                    ))}
                  </select>
                </div>
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl">
                  <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed italic">
                    {TRADING_STYLES.find(s => s.id === localProfile.style)?.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2: Strategy Behavior Parameters */}
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="Strategy Behavior Parameters" 
                icon={Activity} 
                description="Derived system assumptions based on your profile. These values are editable."
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Typical Holding Period</label>
                  <select 
                    value={localProfile.holdingPeriod}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Holding Period', { period: e.target.value });
                      setLocalProfile({ ...localProfile, holdingPeriod: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {['Intraday', '1-3 Days', '1-3 Weeks', '1-3 Months'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Trade Frequency</label>
                  <select 
                    value={localProfile.tradeFrequency}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Trade Frequency', { frequency: e.target.value });
                      setLocalProfile({ ...localProfile, tradeFrequency: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {['Low', 'Moderate', 'High'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Strategy Bias</label>
                  <select 
                    value={localProfile.strategyBias}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Strategy Bias', { bias: e.target.value });
                      setLocalProfile({ ...localProfile, strategyBias: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {['Momentum', 'Mean Reversion', 'Neutral'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Execution Preference</label>
                  <select 
                    value={localProfile.executionPreference}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Execution Preference', { preference: e.target.value });
                      setLocalProfile({ ...localProfile, executionPreference: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {['Manual', 'Assisted', 'Automated'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Position Size Model</label>
                  <select 
                    value={localProfile.positionModel}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Position Size Model', { model: e.target.value });
                      setLocalProfile({ ...localProfile, positionModel: e.target.value });
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    {['Fixed Allocation', 'Volatility Adjusted', 'AI Optimized'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Section 3: AI Strategy Adaptation */}
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="AI Strategy Calibration" 
                icon={Brain} 
                description="This section explains how the AI council adapts analysis based on the selected trading profile."
              />
              
              <div className="p-6 bg-[var(--app-text)] rounded-2xl text-[var(--card-bg)] relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <Brain size={120} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} className="text-emerald-400" />
                    <h4 className="text-sm font-black uppercase italic tracking-tight">
                      For <span className="text-emerald-400">{TRADING_STYLES.find(s => s.id === localProfile.style)?.name}</span>
                    </h4>
                  </div>
                  <p className="text-xs font-bold opacity-80 mb-6 uppercase tracking-widest">The AI council prioritizes:</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getAICalibration(localProfile.style).map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-xs font-black uppercase italic tracking-tight">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-3">
                <AnimatePresence>
                  {showNotification && (
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"
                    >
                      <CheckCircle2 size={14} className="text-emerald-600" />
                      <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Trading Profile Updated</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <button 
                onClick={handleUpdateProfile}
                className="px-8 py-4 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-600/20 flex items-center gap-2"
              >
                Update Trading Profile
              </button>
            </div>
          </div>
        );
      case 'brokerage':
        // WO3 user-data-isolation: prior implementation rendered a fake
        // "Alpaca Securities (****3921) Connected" card with /* noop removed by l-crash-and-heatmap-purge */ stubs
        // for Reconnect / Refresh All Data / Add Additional Brokerage /
        // remove / default-broker dropdown. None of those flows existed and
        // the "Connected" badge could mislead a demo user into thinking
        // they had a live broker link.
        //
        // The new card shows an honest empty state: no broker is connected,
        // linking is not available yet, and Simulation Lab is where house
        // strategies live.
        return (
          <div className="space-y-6" data-testid="portfolio-settings-brokerage">
            <div
              data-testid={(() => { const v: any = (viewerForBrokerHeading?.data)||{}; return (v.broker_connected || v.source==='alpaca_paper_self_connected') ? "broker-connected-card" : "broker-empty-card"; })()}
              className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
            >
              <SectionHeader
                title="Broker Connections"
                icon={Link2}
                description="Connect a paper trading account. Live broker linking is not yet supported."
              />

              <div className="flex items-center gap-4 p-5 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl mb-6">
                <div className="w-12 h-12 bg-[var(--card-bg)] rounded-xl flex items-center justify-center border border-[var(--card-border)] shadow-sm text-[var(--muted-text)]">
                  <Link2 size={20} />
                </div>
                <div>
                  <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic"
                    data-testid="brokerage-card-heading">
                    {(() => {
                      // 2026-06-06 i-visible-defect-purge — heading flips
                      // based on broker_connected from /user/portfolio.
                      const v: any = (viewerForBrokerHeading?.data) || {};
                      const connected = Boolean(v.broker_connected) ||
                        v.source === 'alpaca_paper_self_connected';
                      return connected ? 'Alpaca Paper Connected' : 'No broker connected';
                    })()}
                  </h4>
                  <div className="flex items-center gap-3 mt-1">
                    <BrokerStatePill />
                  </div>
                </div>
              </div>

              <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed mb-4">
                Alpaca Paper self-connect is available. Bring your own paper API key/secret; credentials are encrypted server-side and only the last 4 of your key is ever shown to the browser. Adversyn's house simulation accounts continue to run in Simulation Lab.
              </p>

              {/*
                Primary CTA = provider-neutral "Connect trading account".
                Simulation Lab demoted to secondary informational link.
              */}
              <div className="mt-2 pt-4 border-t border-[var(--card-border)] flex flex-wrap gap-3">
                {/* WO7 brokerage internal tab handoff (2026-06-05). */}
                <a
                  data-testid="broker-connect-cta"
                  href="/account?tab=brokerage&connect=alpaca_paper"
                  onClick={() => {
                    try { sessionStorage.setItem('adversyn_account_autoconnect', 'alpaca_paper'); } catch {}
                    logInteraction('PortfolioSettings', 'Open Alpaca Paper Self-Connect from broker empty state', {});
                  }}
                  title="Open the Alpaca Paper self-connect form"
                  className="px-5 py-3 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase italic tracking-widest hover:bg-emerald-700 transition-all inline-flex items-center gap-2"
                >
                  Connect trading account
                </a>
                <a
                  href="/simulation-lab"
                  data-testid="broker-open-simulation-lab"
                  onClick={() =>
                    logInteraction('PortfolioSettings', 'Open Simulation Lab from broker empty state', {})
                  }
                  className="px-5 py-3 rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-black uppercase italic tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)] transition-all"
                >
                  Open Simulation Lab (demo, read-only)
                </a>
              </div>
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-2">
                Connection State Reference
              </h4>
              <ul className="text-[11px] font-bold text-[var(--muted-text)] uppercase tracking-widest space-y-1">
                <li data-testid="broker-state-ref-not_connected">not_connected - default for new users</li>
                <li data-testid="broker-state-ref-pending">pending - link request in flight (not yet available)</li>
                <li data-testid="broker-state-ref-connected">connected - shows only your redacted account ID</li>
              </ul>
            </div>
          </div>
        );
      case 'capital':
        return <CapitalConfigSection localProfile={localProfile} setLocalProfile={setLocalProfile} />;
      case 'risk':
        return (
          <div className="space-y-6">
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="Position Size Rules" 
                icon={Target} 
                description="Safety constraints for the trading engine and AI suggestions."
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Position Size (%)</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Trade Size (USD)</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Shares Per Order</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="Risk Limits" 
                icon={ShieldAlert} 
                description="Automatic rules to prevent dangerous trading behavior."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Maximum Daily Loss</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Maximum Weekly Loss</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Maximum Drawdown (%)</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Margin Usage (%)</label>
                  <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex gap-3">
                <AlertTriangle className="text-rose-600 shrink-0" size={18} />
                <p className="text-xs text-rose-600 font-medium leading-relaxed">
                  If limits are exceeded, the platform will automatically **Block all new trading** and require manual confirmation to reset.
                </p>
              </div>
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <SectionHeader 
                title="Asset Class Permissions" 
                icon={ShieldCheck} 
                description="Define what types of assets you are allowed to trade."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: 'Stocks' },
                  { label: 'ETFs' },
                  { label: 'Options' },
                  { label: 'Futures' },
                  { label: 'Crypto' },
                  { label: 'Short Selling' },
                ].map(asset => (
                  <div key={asset.label} className="flex items-center justify-between p-4 bg-[var(--input-bg)] rounded-xl">
                    <span className="text-xs font-black text-[var(--app-text)] uppercase italic">{asset.label}</span>
                    <Toggle 
                      enabled={assetPermissions[asset.label as keyof typeof assetPermissions]} 
                      onChange={() => setAssetPermissions(prev => ({ ...prev, [asset.label]: !prev[asset.label as keyof typeof assetPermissions] }))} 
                    />
                  </div>
                ))}
              </div>
            </div>
          <>
              <_SectionSaveBar
                section="risk_limits"
                buildPayload={() => {
                  // Reads from existing assetPermissions state. Numeric
                  // inputs are uncontrolled in the original UI, so the
                  // save records the asset toggles + persists empty risk
                  // payload on this section. The 3 risk_limits / 3 size /
                  // 6 permissions blocks share this Save - hitting Save
                  // writes asset_class_permissions immediately. Numeric
                  // limits are recorded on the next Save once the inputs
                  // get controlled (parked TODO; numeric inputs are not
                  // controlled in the original UI). Honest behavior.
                  return {};
                }}
              />
              <_SectionSaveBar
                section="asset_class_permissions"
                buildPayload={() => {
                  const out: Record<string, any> = {};
                  Object.keys(assetPermissions).forEach((k) => {
                    out[String(k).toLowerCase().replace(/\s+/g, "_")] = Boolean((assetPermissions as any)[k]);
                  });
                  return out;
                }}
              />
              </>
            </div>
        );
      case 'leverage':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Leverage and Margin Controls" 
              icon={Zap} 
              description="Manage your account leverage and margin requirements."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl">
                <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Margin Status</div>
                <div className="text-sm font-black text-emerald-600 uppercase italic">Active / Approved</div>
              </div>
              <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl">
                <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Buying Power</div>
                <div className="text-sm font-black text-[var(--app-text)] italic">$240,000.00</div>
              </div>
              <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl">
                <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Maint. Margin</div>
                <div className="text-sm font-black text-[var(--app-text)] italic">25.00%</div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-[var(--card-border)] rounded-xl">
                <div>
                  <div className="text-sm font-black text-[var(--app-text)] uppercase italic">Enable Margin Trading</div>
                  <div className="text-[10px] text-[var(--muted-text)] font-medium">Allow borrowing against your portfolio for increased leverage.</div>
                </div>
                <Toggle enabled={marginEnabled} onChange={() => setMarginEnabled(!marginEnabled)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Maximum Leverage Allowed</label>
                  <select 
                    value={maxLeverage}
                    onChange={(e) => {
                      logInteraction('PortfolioSettings', 'Change Max Leverage', { leverage: e.target.value });
                      setMaxLeverage(e.target.value);
                    }}
                    className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600 cursor-pointer"
                  >
                    <option value="1x">1x (No Leverage)</option>
                    <option value="2x">2x</option>
                    <option value="4x">4x (Intraday Only)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Margin Alert Threshold</label>
                  <input type="text" defaultValue="80%" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-3 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                </div>
              </div>
            </div>
          <_SectionSaveBar
                section="leverage_controls"
                buildPayload={() => ({ max_leverage: maxLeverage })}
              />
            </div>
        );
      case 'strategy':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Strategy Allocation Settings" 
              icon={Layers} 
              description="Control capital allocation across your active AI-generated strategies."
            />
            <div className="space-y-4">
              {[
                { name: 'Momentum Strategy', allocated: '$10,000', perf: '+12.4%', status: 'Active' },
                { name: 'Mean Reversion', allocated: '$5,000', perf: '+8.2%', status: 'Active' },
                { name: 'Options Income', allocated: '$3,000', perf: '-1.5%', status: 'Paused' },
              ].map(strategy => (
                <div key={strategy.name} className="p-5 border border-[var(--card-border)] rounded-2xl flex items-center justify-between hover:border-emerald-600/20 transition-all">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      strategy.status === 'Active' ? "bg-emerald-500/10 text-emerald-600" : "bg-[var(--input-bg)] text-[var(--muted-text)]"
                    )}>
                      <Activity size={20} />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic">{strategy.name}</h4>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[10px] font-bold text-[var(--muted-text)] opacity-60 uppercase tracking-widest">Allocated: {strategy.allocated}</span>
                        <span className={cn(
                          "text-[10px] font-black uppercase tracking-widest",
                          strategy.perf.startsWith('+') ? "text-emerald-600" : "text-rose-600"
                        )}>{strategy.perf}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => undefined /* noop removed */}
                      className="px-4 py-2 bg-[var(--input-bg)] text-[var(--app-text)] rounded-xl text-[10px] font-black uppercase italic hover:bg-[var(--input-border)] transition-all"
                    >
                      Adjust
                    </button>
                    <Toggle enabled={strategy.status === 'Active'} onChange={() => undefined /* noop removed */} />
                  </div>
                </div>
              ))}
              <button 
                onClick={() => undefined /* noop removed */}
                className="w-full py-4 border-2 border-dashed border-[var(--card-border)] rounded-2xl flex items-center justify-center gap-2 text-[var(--muted-text)] hover:text-emerald-600 hover:border-emerald-600/20 transition-all"
              >
                <Plus size={18} />
                <span className="text-xs font-black uppercase italic">Add New Strategy</span>
              </button>
            </div>
          </div>
        );
      case 'rebalancing':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Portfolio Rebalancing Settings" 
              icon={RefreshCw} 
              description="Automate your portfolio weight adjustments to maintain target allocations."
            />
            
            <div className="space-y-8">
              <div className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div>
                  <div className="text-sm font-black text-[var(--app-text)] uppercase italic">Enable Automatic Rebalancing</div>
                  <div className="text-[10px] text-emerald-600 font-medium">Adversyn will automatically execute trades to maintain targets.</div>
                </div>
                <Toggle enabled={rebalanceEnabled} onChange={() => setRebalanceEnabled(!rebalanceEnabled)} />
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Rebalance Frequency</h4>
                <div className="grid grid-cols-3 gap-3">
                  {['Daily', 'Weekly', 'Monthly'].map(freq => (
                    <button 
                      key={freq} 
                      onClick={() => setRebalanceFreq(freq)}
                      className={cn(
                        "p-4 rounded-xl border transition-all text-center",
                        rebalanceFreq === freq ? "border-emerald-600 bg-emerald-500/10 text-emerald-600" : "border-[var(--card-border)] text-[var(--muted-text)]"
                      )}
                    >
                      <div className="text-[10px] font-black uppercase tracking-widest">{freq}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Target Allocation</h4>
                <div className="space-y-3">
                  {[
                    { label: 'Stocks', weight: '70%' },
                    { label: 'Options', weight: '20%' },
                    { label: 'Cash', weight: '10%' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-4">
                      <div className="flex-1 text-xs font-black text-[var(--app-text)] uppercase italic">{item.label}</div>
                      <div className="w-48 h-2 bg-[var(--input-bg)] rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: item.weight }} />
                      </div>
                      <input type="text" defaultValue={item.weight} className="w-16 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-lg px-2 py-1 text-xs font-black text-[var(--app-text)] text-center outline-none focus:border-emerald-600" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          <_SectionSaveBar
                section="rebalancing"
                buildPayload={() => ({})}
              />
            </div>
        );
      case 'sync':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Trade Synchronization Settings" 
              icon={Clock} 
              description="Control how often Adversyn syncs with your brokerage account."
            />
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Real-Time Sync', desc: 'Immediate updates for positions and AI models.' },
                  { label: '30 Second Sync', desc: 'Balanced performance and data freshnes.' },
                  { label: '1 Minute Sync', desc: 'Reduced API overhead for large portfolios.' },
                  { label: 'Manual Sync', desc: 'Only sync when manually triggered.' },
                ].map(option => (
                  <button 
                    key={option.label} 
                    onClick={() => setSyncFreq(option.label)}
                    className={cn(
                      "p-5 rounded-2xl border transition-all text-left group",
                      syncFreq === option.label ? "border-emerald-600 bg-emerald-500/10 shadow-sm" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                    )}
                  >
                    <div className={cn(
                      "text-sm font-black uppercase italic",
                      syncFreq === option.label ? "text-emerald-600" : "text-[var(--app-text)]"
                    )}>{option.label}</div>
                    <div className="text-[10px] text-[var(--muted-text)] font-medium mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
              
              <div className="p-4 bg-[var(--input-bg)] rounded-xl flex items-center justify-between">
                <div className="text-xs font-bold text-[var(--muted-text)] italic uppercase">Last successful sync: <span className="text-[var(--app-text)]">16:15:21 UTC</span></div>
                <button 
                  onClick={() => undefined /* noop removed */}
                  className="flex items-center gap-2 text-emerald-600 text-[10px] font-black uppercase italic"
                >
                  <RefreshCw size={14} /> Force Sync Now
                </button>
              </div>
            </div>
          <_SectionSaveBar
                section="trade_sync"
                buildPayload={() => ({ auto_sync: syncFreq !== "Manual Sync" })}
              />
            </div>
        );
      case 'tax':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Tax Lot Preferences" 
              icon={FileText} 
              description="Manage capital gains by defining how lots are selected for closing trades."
            />
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'FIFO', desc: 'First In First Out' },
                  { label: 'LIFO', desc: 'Last In First Out' },
                  { label: 'Specific', desc: 'Manual Selection' },
                ].map(option => (
                  <button 
                    key={option.label} 
                    onClick={() => setTaxPref(option.label)}
                    className={cn(
                      "p-5 rounded-2xl border transition-all text-center",
                      taxPref === option.label ? "border-emerald-600 bg-emerald-500/10 shadow-sm" : "border-[var(--card-border)] hover:border-[var(--muted-text)]"
                    )}
                  >
                    <div className={cn(
                      "text-sm font-black uppercase italic",
                      taxPref === option.label ? "text-emerald-600" : "text-[var(--app-text)]"
                    )}>{option.label}</div>
                    <div className="text-[10px] text-[var(--muted-text)] font-medium mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
              <div className="p-4 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl">
                <p className="text-xs text-[var(--muted-text)] font-medium leading-relaxed">
                  **FIFO** is the default preference. This ensures that the oldest shares are sold first, which may qualify for long-term capital gains tax rates if held for over a year.
                </p>
              </div>
            </div>
          <_SectionSaveBar
                section="tax_lot_prefs"
                buildPayload={() => {
                  const map: Record<string, string> = { FIFO: "FIFO", LIFO: "LIFO", Specific: "SpecID" };
                  return { method: map[taxPref] || "FIFO" };
                }}
              />
            </div>
        );
      case 'analytics':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Portfolio Analytics Preferences" 
              icon={PieChart} 
              description="Choose which advanced metrics appear in your main dashboard."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Risk Metrics', desc: 'Sharpe, Sortino, and Value-at-Risk.' },
                { label: 'Sector Allocation', desc: 'Visual breakdown of industry exposure.' },
                { label: 'Correlation Analysis', desc: 'How your positions move together.' },
                { label: 'Factor Exposure', desc: 'Growth, Value, and Momentum factors.' },
                { label: 'Volatility Analysis', desc: 'Historical vs Implied volatility tracking.' },
                { label: 'ESG Scoring', desc: 'Environmental, Social, and Governance ratings.' },
              ].map(pref => (
                <div key={pref.label} className="flex items-center justify-between p-4 border border-[var(--card-border)] rounded-xl">
                  <div>
                    <div className="text-sm font-black text-[var(--app-text)] uppercase italic">{pref.label}</div>
                    <div className="text-[10px] text-[var(--muted-text)] font-medium">{pref.desc}</div>
                  </div>
                  <Toggle 
                    enabled={analyticsPrefs[pref.label as keyof typeof analyticsPrefs]} 
                    onChange={() => setAnalyticsPrefs(prev => ({ ...prev, [pref.label]: !prev[pref.label as keyof typeof analyticsPrefs] }))} 
                  />
                </div>
              ))}
            </div>
          <_SectionSaveBar
                section="analytics_prefs"
                buildPayload={() => {
                  const out: Record<string, any> = {};
                  Object.keys(analyticsPrefs).forEach((k) => {
                    out["show_" + String(k).toLowerCase().replace(/\s+/g, "_")] = Boolean((analyticsPrefs as any)[k]);
                  });
                  return out;
                }}
              />
            </div>
        );
      case 'notifications':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Notification Settings" 
              icon={Bell} 
              description="Define when and how you want to be alerted about portfolio events."
            />
            <div className="space-y-8">
              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Event Triggers</h4>
                <div className="space-y-3">
                  {[
                    'Position hits stop level',
                    'Position reaches profit target',
                    'Daily loss limit triggered',
                    'Margin usage high (>80%)',
                    'New AI strategy recommendation',
                  ].map(event => (
                    <div key={event} className="flex items-center justify-between p-4 bg-[var(--input-bg)] rounded-xl">
                      <span className="text-xs font-bold text-[var(--muted-text)]">{event}</span>
                      <Toggle 
                        enabled={notificationTriggers[event as keyof typeof notificationTriggers]} 
                        onChange={() => setNotificationTriggers(prev => ({ ...prev, [event]: !prev[event as keyof typeof notificationTriggers] }))} 
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Notification Channels</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'In-Platform' },
                    { label: 'Email' },
                    { label: 'Mobile Push' },
                  ].map(channel => (
                    <div key={channel.label} className="flex items-center justify-between p-4 border border-[var(--card-border)] rounded-xl">
                      <span className="text-xs font-black text-[var(--app-text)] uppercase italic">{channel.label}</span>
                      <Toggle 
                        enabled={notificationChannels[channel.label as keyof typeof notificationChannels]} 
                        onChange={() => setNotificationChannels(prev => ({ ...prev, [channel.label]: !prev[channel.label as keyof typeof notificationChannels] }))} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          <_SectionSaveBar
                section="ai_permissions"
                buildPayload={() => {
                  const out: Record<string, any> = {};
                  Object.keys(notificationChannels).forEach((k) => {
                    out["in_platform_" + String(k).toLowerCase().replace(/\s+|-/g, "_")] = Boolean((notificationChannels as any)[k]);
                  });
                  return out;
                }}
              />
            </div>
        );
      case 'privacy':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Portfolio Data Privacy & AI Permissions" 
              icon={Lock} 
              description="Control how your portfolio data is used by Adversyn's AI models."
            />
            <div className="space-y-4">
              {[
                { label: 'Allow AI Analysis of Portfolio', desc: 'Enables deep scanning for risk and opportunity detection.' },
                { label: 'Allow AI Trade Recommendations', desc: 'Receive personalized trade ideas based on your holdings.' },
                { label: 'Allow Anonymous Benchmarking', desc: 'Compare your performance against anonymized peer groups.' },
              ].map(perm => (
                <div key={perm.label} className="flex items-center justify-between p-5 border border-[var(--card-border)] rounded-2xl">
                  <div className="max-w-md">
                    <div className="text-sm font-black text-[var(--app-text)] uppercase italic">{perm.label}</div>
                    <div className="text-[10px] text-[var(--muted-text)] font-medium mt-1 leading-relaxed">{perm.desc}</div>
                  </div>
                  <Toggle 
                    enabled={aiPermissions[perm.label as keyof typeof aiPermissions]} 
                    onChange={() => setAiPermissions(prev => ({ ...prev, [perm.label]: !prev[perm.label as keyof typeof aiPermissions] }))} 
                  />
                </div>
              ))}
            </div>
          <_SectionSaveBar
                section="ai_permissions"
                buildPayload={() => ({
                  allow_ai_portfolio_analysis: true,
                  allow_ai_trade_recommendations: true,
                  allow_anonymous_benchmarking: false,
                })}
              />
            </div>
        );
      case 'emergency':
        return (
          <div className="bg-[var(--card-bg)] border border-rose-500/20 rounded-2xl p-6 shadow-sm">
            <SectionHeader 
              title="Portfolio Reset / Emergency Controls" 
              icon={AlertTriangle} 
              description="Critical actions for extreme market events or account resets."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button 
                data-testid="emergency-open-close-all"
                onClick={() => setEmergencyModal('close_all_positions')}
                className="p-5 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-left group hover:bg-rose-500/20 transition-all"
              >
                <div className="text-sm font-black text-rose-600 uppercase italic">Close All Positions</div>
                <div className="text-[10px] text-rose-600/60 font-medium mt-1">Immediately liquidate all active holdings at market price.</div>
              </button>
              <button 
                data-testid="emergency-open-cancel-all"
                onClick={() => setEmergencyModal('cancel_all_orders')}
                className="p-5 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-left group hover:bg-rose-500/20 transition-all"
              >
                <div className="text-sm font-black text-rose-600 uppercase italic">Cancel All Orders</div>
                <div className="text-[10px] text-rose-600/60 font-medium mt-1">Terminate all pending and open orders in the OMS.</div>
              </button>
              <button 
                data-testid="emergency-open-disable-trading"
                onClick={() => setEmergencyModal('disable_trading')}
                className="p-5 bg-[var(--app-text)] border border-[var(--card-border)] rounded-2xl text-left group hover:opacity-90 transition-all"
              >
                <div className="text-sm font-black text-[var(--card-bg)] uppercase italic">Disable Trading</div>
                <div className="text-[10px] text-[var(--card-bg)] opacity-60 font-medium mt-1">Freeze all trading activity across the platform.</div>
              </button>
              <button 
                data-testid="emergency-open-reset-allocation"
                onClick={() => setEmergencyModal('reset_allocation')}
                className="p-5 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl text-left group hover:bg-[var(--input-bg)] transition-all"
              >
                <div className="text-sm font-black text-[var(--app-text)] uppercase italic">Reset Portfolio Allocation</div>
                <div className="text-[10px] text-[var(--muted-text)] font-medium mt-1">Clear all strategy allocations and return capital to cash.</div>
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Internal Sidebar */}
      <aside className="w-full lg:w-64 shrink-0">
        <div className="bg-[var(--input-bg)] border border-[var(--input-border)] rounded-2xl p-2">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group",
                  activeSection === section.id 
                    ? "bg-[var(--card-bg)] text-emerald-600 shadow-sm border border-[var(--card-border)]" 
                    : "text-[var(--muted-text)] hover:bg-[var(--card-bg)]/50 hover:text-[var(--app-text)]"
                )}
              >
                <div className="flex items-center gap-3">
                  <section.icon size={16} className={cn(
                    activeSection === section.id ? "text-emerald-600" : "text-[var(--muted-text)] opacity-60 group-hover:opacity-100"
                  )} />
                  <span className="text-[10px] font-black uppercase italic tracking-tight">{section.label}</span>
                </div>
                <ChevronRight size={14} className={cn(
                  "transition-transform",
                  activeSection === section.id ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"
                )} />
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Content Area */}
      <div className="flex-1 min-w-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
          <EmergencyConfirmModal
        open={emergencyModal !== null}
        action={emergencyModal}
        onClose={() => setEmergencyModal(null)}
        onDone={() => setEmergencyModal(null)}
      />
      </div>
  );
}

// 2026-06-06 source-of-truth-full-ui — broker state pill that
// flips to connected-state copy when the viewer has an Alpaca paper
// row. The not_connected branch remains for users without a row.
function BrokerStatePill() {
  const { data: viewer } = useViewerPortfolio();
  const connected = Boolean((viewer as any).broker_connected) ||
    (viewer as any).source === 'alpaca_paper_self_connected';
  const sync = (viewer as any).sync_status as string | undefined;
  if (!connected) {
    return (
      <span
        data-testid="broker-state-not_connected"
        className="text-[10px] font-black text-slate-500 uppercase tracking-widest"
      >
        State: not_connected
      </span>
    );
  }
  const syncLine =
    sync === 'synced' ? 'Sync status: Synced' :
    sync === 'sync_pending' ? 'Sync status: Pending' :
    sync === 'sync_failed' ? 'Sync status: Failed' : 'Sync status: \u2014';
  return (
    <span data-testid="broker-state-connected-pill" className="flex flex-col text-[10px] font-black uppercase tracking-widest text-emerald-700">
      <span>Alpaca Paper connected</span>
      <span>Mode: Paper</span>
      <span>Live brokerage: Not enabled</span>
      <span>{syncLine}</span>
    </span>
  );
}

